import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Paper,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  Switch,
  FormControlLabel
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LeftMenu from "../../components/left-menu/index.js";
import axios from "axios";
import { LocalConvenienceStoreOutlined } from "@mui/icons-material";

/** We clamp images/videos to a max height of 360 px. */
const MAX_HEIGHT = 360;

/** Check file extension to see if it’s likely a video */
function isVideoKey(keyOrUrl) {
  const ext = keyOrUrl.split(".").pop()?.toLowerCase() || "";
  return ["mp4", "webm", "mov", "avi", "m4v", "ogg"].includes(ext);
}

/**
 * Create a screenshot (dataURL) for the first second of a video at `videoUrl`.
 * Only works if the S3 bucket is CORS-enabled for <canvas>.
 */
async function createVideoThumbnail(videoUrl) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.src = videoUrl;
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      const targetTime = Math.min(1, video.duration || 1);
      video.currentTime = targetTime;
    };

    video.onseeked = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataURL = canvas.toDataURL("image/png");
      resolve(dataURL);
    };

    video.onerror = (e) => {
      console.error("Video error for:", videoUrl, e);
      reject(e);
    };
  });
}

/** Helper for GSA combos */
function cartesianProduct(arrA, arrB) {
  const result = [];
  for (const a of arrA) {
    for (const b of arrB) {
      result.push({ title: a, description: b });
    }
  }
  return result;
}

export default function MarketingDashboard() {
  // ========== PLATFORM SELECTION ==========
  const [platform, setPlatform] = useState("");

  // ========== GSA FIELDS ==========
  const [adCode, setAdCode] = useState("");
  const [adID, setAdID] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [adset, setAdset] = useState("");
  const [keywords, setKeywords] = useState("");
  const [locationTargeting, setLocationTargeting] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");

  // GSA combos
  const [headlines, setHeadlines] = useState([""]);
  const [descriptions, setDescriptions] = useState([""]);
  const [allCombos, setAllCombos] = useState([]);
  const [comboIndex, setComboIndex] = useState(0);
  const intervalRef = useRef(null);

  // ========== META ADS FIELDS ==========
  const [metaAdCode, setMetaAdCode] = useState("");
  const [metaAdID, setMetaAdID] = useState("");
  const [metaTargeting, setMetaTargeting] = useState("");
  const [metaNotes, setMetaNotes] = useState("");
  const [metaPrimaryText, setMetaPrimaryText] = useState("");

  // ========== FILE & RESIZE STATES ==========
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [fileDebugInfo, setFileDebugInfo] = useState({
    type: "",
    size: 0,
    imageWidth: null,
    imageHeight: null,
    videoWidth: null,
    videoHeight: null,
    duration: null
  });

  // PROGRESS BAR STATES (video)
  const [processingVideo, setProcessingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  // ========== Use Existing Media ==========
  const [useExistingMedia, setUseExistingMedia] = useState(false);
  const [mediaList, setMediaList] = useState([]); // from GET /listMedia
  const [selectedMediaKey, setSelectedMediaKey] = useState("");

  // Refs for final dimension checks
  const imgRef = useRef(null);
  const videoRef = useRef(null);

  // ----- CLEAR STATES ON PLATFORM CHANGE -----
  const handlePlatformChange = (e) => {
    setPlatform(e.target.value);

    // Reset many states
    setAdCode("");
    setAdID("");
    setCampaignName("");
    setAdset("");
    setKeywords("");
    setLocationTargeting("");
    setAdditionalNotes("");
    setHeadlines([""]);
    setDescriptions([""]);
    setAllCombos([]);
    setComboIndex(0);

    setMetaAdCode("");
    setMetaAdID("");
    setMetaTargeting("");
    setMetaNotes("");
    setMetaPrimaryText("");

    setUploadedFile(null);
    setPreviewUrl("");
    setFileDebugInfo({
      type: "",
      size: 0,
      imageWidth: null,
      imageHeight: null,
      videoWidth: null,
      videoHeight: null,
      duration: null
    });
    setProcessingVideo(false);
    setVideoProgress(0);

    setUseExistingMedia(false);
    setSelectedMediaKey("");
  };

  // ---------- HEADLINES + DESCRIPTIONS => COMBOS ----------
  useEffect(() => {
    const valHeads = headlines.filter((h) => h.trim());
    const valDescs = descriptions.filter((d) => d.trim());
    const combos = cartesianProduct(valHeads, valDescs);
    setAllCombos(combos);
    setComboIndex(0);
  }, [headlines, descriptions]);

  // Rotate combos every 5s
  useEffect(() => {
    if (allCombos.length < 1) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setComboIndex((prev) => (prev + 1) % allCombos.length);
    }, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [allCombos]);

  const currentCombo = allCombos.length > 0 ? allCombos[comboIndex] : null;

  // ========== HEADLINE/DESCRIPTION HANDLERS ==========
  function addHeadlineField() {
    setHeadlines((prev) => [...prev, ""]);
  }
  function removeHeadlineField(idx) {
    setHeadlines((prev) => prev.filter((_, i) => i !== idx));
  }
  function handleHeadlineChange(idx, val) {
    setHeadlines((prev) => {
      const copy = [...prev];
      copy[idx] = val;
      return copy;
    });
  }

  function addDescriptionField() {
    setDescriptions((prev) => [...prev, ""]);
  }
  function removeDescriptionField(idx) {
    setDescriptions((prev) => prev.filter((_, i) => i !== idx));
  }
  function handleDescriptionChange(idx, val) {
    setDescriptions((prev) => {
      const copy = [...prev];
      copy[idx] = val;
      return copy;
    });
  }

  // ========== RENDER GSA SNIPPET ==========
  function renderGoogleSearchSnippet() {
    if (!currentCombo) {
      return (
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: 1,
            p: 2,
            backgroundColor: "#fafafa",
            minHeight: 180
          }}
        >
          <Typography variant="body2" color="text.secondary">
            (No valid headlines / descriptions yet)
          </Typography>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: 1,
          p: 2,
          backgroundColor: "#fafafa",
          minHeight: 180
        }}
      >
        <Typography variant="subtitle2" sx={{ color: "#2c6dec", mb: 1 }}>
          {currentCombo.title}
        </Typography>
        <Typography variant="body2" sx={{ color: "#006400" }}>
          zanio.co.id
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {currentCombo.description}
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
          (Rotates every 5s) — Combo {comboIndex + 1}/{allCombos.length}
        </Typography>
      </Box>
    );
  }

  // ========== GSA FORM ==========
  function renderGoogleSearchAdsForm() {
    return (
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="AdCode"
              value={adCode}
              onChange={(e) => setAdCode(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="AdID"
              value={adID}
              onChange={(e) => setAdID(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Campaign Name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Adset"
              value={adset}
              onChange={(e) => setAdset(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Keywords"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Location Targeting"
              value={locationTargeting}
              onChange={(e) => setLocationTargeting(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Additional Notes"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>

          {/* RIGHT: Headlines + Descriptions + snippet */}
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              Headlines
            </Typography>
            {headlines.map((val, idx) => (
              <Box key={idx} sx={{ display: "flex", mb: 1 }}>
                <TextField
                  placeholder={`Headline ${idx + 1}`}
                  value={val}
                  onChange={(e) => handleHeadlineChange(idx, e.target.value)}
                  fullWidth
                  sx={{ mr: 1 }}
                />
                {headlines.length > 1 && (
                  <IconButton onClick={() => removeHeadlineField(idx)} aria-label="delete">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button variant="outlined" onClick={addHeadlineField} sx={{ mb: 2 }}>
              + Add Headline
            </Button>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
              Descriptions
            </Typography>
            {descriptions.map((val, idx) => (
              <Box key={idx} sx={{ display: "flex", mb: 1 }}>
                <TextField
                  placeholder={`Description ${idx + 1}`}
                  value={val}
                  onChange={(e) => handleDescriptionChange(idx, e.target.value)}
                  fullWidth
                  sx={{ mr: 1 }}
                />
                {descriptions.length > 1 && (
                  <IconButton onClick={() => removeDescriptionField(idx)} aria-label="delete">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button variant="outlined" onClick={addDescriptionField} sx={{ mb: 2 }}>
              + Add Description
            </Button>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Google Search Preview
            </Typography>
            {renderGoogleSearchSnippet()}
          </Grid>
        </Grid>
      </Box>
    );
  }

  // ========== IMAGE RESIZING (Canvas) ==========
  async function resizeImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const origW = img.width;
        const origH = img.height;
        if (origH <= MAX_HEIGHT) {
          // no resizing needed
          resolve(file);
          return;
        }
        const scale = MAX_HEIGHT / origH;
        const newW = Math.floor(origW * scale);
        const newH = MAX_HEIGHT;

        const canvas = document.createElement("canvas");
        canvas.width = newW;
        canvas.height = newH;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, newW, newH);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject("Canvas toBlob failed");
              return;
            }
            const ext = file.name.split(".").pop() || "png";
            const newName = file.name.replace(/\.\w+$/, "") + `_resized.${ext}`;
            const resizedFile = new File([blob], newName, { type: blob.type });
            resolve(resizedFile);
          },
          file.type,
          0.9
        );
      };
      img.onerror = (err) => reject(err);
      img.src = url;
    });
  }

  // ========== VIDEO "RESIZING" (Canvas + MediaRecorder => WebM) ==========
  async function resizeVideoCanvasMediaRecorderWithAudio(
    file,
    setProcessingVideo,
    setVideoProgress
  ) {
    console.log("[resizeVideoCanvasMediaRecorderWithAudio] Starting video resize logic with audio...");
    setProcessingVideo(true);
    setVideoProgress(0);

    const videoURL = URL.createObjectURL(file);
    const hiddenVideo = document.createElement("video");
    hiddenVideo.src = videoURL;
    hiddenVideo.crossOrigin = "anonymous";
    hiddenVideo.muted = false;
    hiddenVideo.playsInline = true;
    hiddenVideo.autoplay = false;
    hiddenVideo.controls = false;

    // Wait for metadata
    await new Promise((resolve, reject) => {
      hiddenVideo.onloadedmetadata = () => {
        resolve();
      };
      hiddenVideo.onerror = (e) => {
        console.error("[resizeVideoCanvasMediaRecorderWithAudio] onerror", e);
        reject(e);
      };
    });

    const origW = hiddenVideo.videoWidth;
    const origH = hiddenVideo.videoHeight;
    if (origH <= MAX_HEIGHT) {
      // No resizing needed
      setProcessingVideo(false);
      return file;
    }

    // Scale factor
    const scale = MAX_HEIGHT / origH;
    const newW = Math.floor(origW * scale);
    const newH = MAX_HEIGHT;

    const canvas = document.createElement("canvas");
    canvas.width = newW;
    canvas.height = newH;
    const ctx = canvas.getContext("2d");

    // Canvas stream
    const canvasStream = canvas.captureStream();
    const canvasVideoTrack = canvasStream.getVideoTracks()[0];

    // Original video stream
    const videoStream = hiddenVideo.captureStream();
    const audioTracks = videoStream.getAudioTracks();

    // Combine canvas + audio
    const combinedStream = new MediaStream();
    if (canvasVideoTrack) {
      combinedStream.addTrack(canvasVideoTrack);
    }
    audioTracks.forEach((track) => combinedStream.addTrack(track));

    const mediaRecorder = new MediaRecorder(combinedStream, {
      videoBitsPerSecond: 2_000_000 // 2 Mbps
    });

    const chunks = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    // Start video
    await hiddenVideo.play();
    mediaRecorder.start(100);

    await new Promise((resolve) => {
      const drawFrame = () => {
        if (hiddenVideo.ended || hiddenVideo.currentTime >= hiddenVideo.duration) {
          resolve();
          return;
        }
        ctx.drawImage(hiddenVideo, 0, 0, newW, newH);
        // Update progress
        const fraction = hiddenVideo.currentTime / hiddenVideo.duration;
        setVideoProgress(Math.round(fraction * 100));
        requestAnimationFrame(drawFrame);
      };
      drawFrame();
    });

    setVideoProgress(100);

    await new Promise((resolve) => {
      mediaRecorder.onstop = resolve;
      mediaRecorder.stop();
    });

    const finalBlob = new Blob(chunks, { type: "video/webm" });
    const newName = file.name.replace(/\.\w+$/, "") + "_resized_withAudio.webm";
    const finalFile = new File([finalBlob], newName, { type: "video/webm" });

    setProcessingVideo(false);
    return finalFile;
  }

  // ========== handleFileChange ==========
  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    // skip .mov
    if (file.type.includes("quicktime")) {
      alert("Unsupported file type (.mov). Please use .mp4 or an image!");
      return;
    }

    let finalFile = file;
    try {
      if (file.type.startsWith("image/")) {
        finalFile = await resizeImage(file);
      } else if (file.type.startsWith("video/")) {
        finalFile = await resizeVideoCanvasMediaRecorderWithAudio(
          file,
          setProcessingVideo,
          setVideoProgress
        );
      }
    } catch (err) {
      console.error("Error during resizing:", err);
      finalFile = file; // fallback => no resizing
    }

    setUploadedFile(finalFile);
    setFileDebugInfo({
      type: finalFile.type,
      size: finalFile.size,
      imageWidth: null,
      imageHeight: null,
      videoWidth: null,
      videoHeight: null,
      duration: null
    });
    const objectUrl = URL.createObjectURL(finalFile);
    setPreviewUrl(objectUrl);
  }

  function handleImageLoad() {
    if (!imgRef.current) return;
    const w = imgRef.current.naturalWidth;
    const h = imgRef.current.naturalHeight;
    setFileDebugInfo((prev) => ({
      ...prev,
      imageWidth: w,
      imageHeight: h
    }));
  }

  // how many items to show at once
const [itemsToShow, setItemsToShow] = useState(6);


  function handleVideoLoad() {
    if (!videoRef.current) return;
    const w = videoRef.current.videoWidth;
    const h = videoRef.current.videoHeight;
    const d = videoRef.current.duration;
    setFileDebugInfo((prev) => ({
      ...prev,
      videoWidth: w,
      videoHeight: h,
      duration: d
    }));
  }

  // ========== FETCH MEDIA LIST ON MOUNT ==========
  useEffect(() => {
    (async function fetchMediaList() {
      try {
        // 1) Fetch the list of media items (which only has item.key)
        const resp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/listMedia`);
        let items = resp.data || [];
  
        // 2) For each item, call "/getmediaurl" to get a presigned URL & contentType
        const processed = await Promise.all(
          items.map(async (item) => {
            try {
              // Hit your existing getmediaurl route
              const getResp = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/getmediaurl?key=${encodeURIComponent(item.key)}`
              );
              const { url, contentType } = getResp.data;
  
              // Now decide if it’s video or image based on contentType
              if (contentType.startsWith("video/")) {
                // Attempt to create a thumbnail for the video
                try {
                  const thumbUrl = await createVideoThumbnail(url);
                  return { ...item, thumbnailUrl: thumbUrl, isVideo: true, signedUrl: url };
                } catch {
                  // If thumbnail creation fails, just use the direct video URL as fallback
                  return { ...item, thumbnailUrl: url, isVideo: true, signedUrl: url };
                }
              } else {
                // It's an image => thumbnail = the same presigned URL
                return { ...item, thumbnailUrl: url, isVideo: false, signedUrl: url };
              }
            } catch (err) {
              console.error("Failed to get signed URL for", item.key, err);
              // fallback if there's an error
              return { ...item, thumbnailUrl: "", isVideo: false, signedUrl: "" };
            }
          })
        );
  
        // 3) Store the processed array in state
        setMediaList(processed);
      } catch (err) {
        console.error("Error fetching media list:", err);
      }
    })();
  }, []);
  

  // ========== NEW: FETCH PRESIGNED GET URL FOR SELECTED KEY ==========
  // We'll call this whenever a user clicks an existing media thumbnail
  async function handleSelectMediaItem(item) {
    try {
      setSelectedMediaKey(item.key);
      setPreviewUrl(""); // clear old preview while we fetch the new one

      // Call your /getmediaurl route (which should return a GET URL)
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/getmediaurl?key=${encodeURIComponent(item.key)}`
      );
      const signedGetUrl = resp.data; // the presigned GET URL
      console.log(signedGetUrl)

      // Now we can display that in <video> or <img>
      setPreviewUrl(signedGetUrl);
    } catch (err) {
      console.error("Failed to get download url:", err);
      alert("Error fetching media for preview. Check logs.");
    }
  }

  // ========== META ADS FORM ==========
  function renderMetaAdsForm() {
    return (
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {/* Left side */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="AdCode"
              value={metaAdCode}
              onChange={(e) => setMetaAdCode(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="AdID"
              value={metaAdID}
              onChange={(e) => setMetaAdID(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Targeting"
              value={metaTargeting}
              onChange={(e) => setMetaTargeting(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Primary Text"
              value={metaPrimaryText}
              onChange={(e) => setMetaPrimaryText(e.target.value)}
              multiline
              rows={3}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Additional Notes"
              value={metaNotes}
              onChange={(e) => setMetaNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={useExistingMedia}
                  onChange={(event) => {
                    setUseExistingMedia(event.target.checked);
                    if (event.target.checked) {
                      // user wants existing => clear new upload
                      setUploadedFile(null);
                      setPreviewUrl("");
                    } else {
                      setSelectedMediaKey("");
                    }
                  }}
                />
              }
              label="Use Existing Media?"
            />

            {/* If NOT using existing => show upload button */}
            {!useExistingMedia && (
              <>
                <Button variant="contained" component="label" sx={{ mt: 2 }}>
                  Upload Image / Video
                  <input hidden accept="image/*,video/*" type="file" onChange={handleFileChange} />
                </Button>

                {processingVideo && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Resizing Video...</Typography>
                    <LinearProgress variant="determinate" value={videoProgress} />
                    <Typography variant="body2">{videoProgress}%</Typography>
                  </Box>
                )}

                {/* Debug info */}
                {uploadedFile && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Debug File Info:</Typography>
                    <Typography variant="body2">Type: {fileDebugInfo.type}</Typography>
                    <Typography variant="body2">Size: {fileDebugInfo.size} bytes</Typography>
                    {fileDebugInfo.imageWidth && fileDebugInfo.imageHeight && (
                      <Typography variant="body2">
                        Resized Image: {fileDebugInfo.imageWidth} x {fileDebugInfo.imageHeight}
                      </Typography>
                    )}
                    {fileDebugInfo.videoWidth && fileDebugInfo.videoHeight && (
                      <Typography variant="body2">
                        Canvas-based WebM: {fileDebugInfo.videoWidth} x {fileDebugInfo.videoHeight},{" "}
                        dur: {fileDebugInfo.duration?.toFixed(2)}s
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}

            {/* THUMBNAIL GALLERY */}
            {useExistingMedia && (
  <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle1" gutterBottom>
      Pick an existing media:
    </Typography>
    
    <Grid container spacing={2}>
      {mediaList.slice(0, itemsToShow).map((item) => (
        <Grid item xs={6} sm={4} md={3} key={item.key}>
          <Box
            onClick={() => handleSelectMediaItem(item)}
            sx={{
              border: selectedMediaKey === item.key ? "2px solid blue" : "1px solid #ccc",
              borderRadius: 1,
              cursor: "pointer",
              overflow: "hidden"
            }}
          >
            <img
              src={item.thumbnailUrl}
              alt={item.key}
              style={{ width: "100%", maxHeight: 120, objectFit: "cover" }}
            />
          </Box>
          <Typography variant="caption" noWrap>
            {item.key}
          </Typography>
        </Grid>
      ))}
    </Grid>

    {itemsToShow < mediaList.length && (
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => setItemsToShow(itemsToShow + 6)}>
          Load More
        </Button>
      </Box>
    )}
  </Box>
)}

          </Grid>

          {/* Right side: "Facebook" & "Instagram" previews */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Facebook Post Preview
              </Typography>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: "#fafafa"
                }}
              >
                {/* Header (Avatar + Name + Sponsored) */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: "#ccc",
                      mr: 2
                    }}
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Zanio
                    </Typography>
                  </Box>
                </Box>

                {/* Primary Text */}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {metaPrimaryText || "(Primary text appears here)"}
                </Typography>

                {/* Media Preview (image or video) */}
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: 1,
                    minHeight: "300px",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fff"
                  }}
                >
                  {useExistingMedia && selectedMediaKey ? (
                    // We rely on previewUrl from handleSelectMediaItem
                    (() => {
                      // Check if we're still loading or have an empty URL
                      if (!previewUrl.url) {
                        return <Typography>Loading preview...</Typography>;
                      }
                      // Check if item is a video
                      const found = mediaList.find((x) => x.key === selectedMediaKey);
                      if (!found) {
                        return <Typography>No data for {selectedMediaKey}</Typography>;
                      }
                      if (previewUrl.contentType.startsWith("video/")) {
                        console.log("here video")
                        return (
                          <video
                            src={previewUrl.url}
                            controls
                            style={{ maxWidth: "100%", maxHeight: 360 }}
                          />
                        );
                      } else {
                        return (
                          <img
                            src={previewUrl.url}
                            alt={found.key}
                            style={{ maxWidth: "100%", maxHeight: 360 }}
                          />
                        );
                      }
                    })()
                  ) : previewUrl ? (
                    // If user uploaded a new file => show that local preview
                    uploadedFile?.type.includes("image") ? (
                      <img
                        ref={imgRef}
                        src={previewUrl}
                        alt="Ad Preview"
                        onLoad={handleImageLoad}
                        style={{ maxWidth: "100%", maxHeight: "360px" }}
                      />
                    ) : uploadedFile?.type.includes("video") ? (
                      <video
                        ref={videoRef}
                        src={previewUrl}
                        controls
                        onLoadedMetadata={handleVideoLoad}
                        style={{ maxWidth: "100%", maxHeight: "360px" }}
                      />
                    ) : (
                      <Typography>(File type not supported)</Typography>
                    )
                  ) : (
                    <Typography variant="subtitle2" sx={{ color: "#999" }}>
                      No file uploaded yet
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // ========== handleSave (MAIN LOGIC) ==========
  async function handleSave() {
    try {
      let dataToSave = {};
      let fileKey = null;

      // Decide if we use existing media or upload new
      if (useExistingMedia && selectedMediaKey) {
        fileKey = selectedMediaKey;
      } else if (uploadedFile) {
        const fileType = uploadedFile.type;
        console.log("Requesting presigned URL for:", uploadedFile.name, "type:", fileType);

        const resp = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/getUploadURL?fileType=${fileType}`
        );
        const uploadUrl = resp.data.url;
        fileKey = resp.data.key;

        console.log("Got presigned URL:", uploadUrl, " => uploading to S3...");
        await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": fileType },
          body: uploadedFile
        });

        console.log("Uploaded to S3 successfully, key:", fileKey);
      }

      // 2) GATHER FIELDS & POST TO /saveads
      if (platform === "Google Search Ads") {
        dataToSave = {
          platform: "Google Search Ads",
          adCode,
          adID,
          campaignName,
          adSet: adset,
          keywords,
          locationTargeting,
          additionalNotes,
          headlines,
          descriptions,
          mediaUrl: fileKey || "",
          createdAt: new Date(),
          updatedAt: new Date()
        };
      } else if (platform === "Meta Ads") {
        dataToSave = {
          platform: "Meta Ads",
          adCode: metaAdCode,
          adID: metaAdID,
          metaTargeting,
          metaNotes,
          metaPrimaryText,
          mediaUrl: fileKey || "",
          createdAt: new Date(),
          updatedAt: new Date()
        };
      } else if (platform === "Google Display Video Ads") {
        dataToSave = {
          platform: "Google Display Video Ads",
          message: "TBD",
          mediaUrl: fileKey || "",
          createdAt: new Date(),
          updatedAt: new Date()
        };
      } else {
        alert("Please select a platform first.");
        return;
      }

      // 3) Send data to backend
      console.log("Saving ad data to /saveads => ", dataToSave);
      const saveResp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/saveads`,
        dataToSave
      );

      if (saveResp.status === 200) {
        alert("Ad saved successfully!");
        console.log("Saved doc =>", saveResp.data);
      } else {
        alert("Error saving ad, please check logs.");
        console.error("Error saving ad =>", saveResp);
      }
    } catch (error) {
      console.error("Error in handleSave =>", error);
      alert("Error saving ad or uploading file.");
    }
  }

  return (
    <Grid container>
      {/* Left Menu */}
      <Grid item xl={0.5} lg={0.7} md={0.9} sm={3.2} className="leftmenucolor">
        <LeftMenu />
      </Grid>

      {/* Main content */}
      <Grid item xs={12} sm={8.8} md={11.1} lg={11.3} xl={11.5} sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Marketing Dashboard (Create Ads)
        </Typography>

        <Paper sx={{ p: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Platform</InputLabel>
            <Select value={platform} label="Platform" onChange={handlePlatformChange}>
              <MenuItem value="Google Search Ads">Google Search Ads</MenuItem>
              <MenuItem value="Meta Ads">Meta Ads</MenuItem>
              <MenuItem value="Google Display Video Ads">Google Display Video Ads</MenuItem>
            </Select>
          </FormControl>

          {platform === "Google Display Video Ads" && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              (Still Under Construction)
            </Typography>
          )}
          {platform === "Google Search Ads" && renderGoogleSearchAdsForm()}
          {platform === "Meta Ads" && renderMetaAdsForm()}

          <Divider sx={{ my: 3 }} />
          <Box sx={{ textAlign: "right" }}>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}
